import React, { useState } from 'react';
import SbEditable from 'storyblok-react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Sentry from '@sentry/react';
import InputMask from 'react-input-mask';
import * as Yup from 'yup';

import { leads, metrics } from '../../services';
import TrackFormDirty from '../TrackFormDirty';
import { PhoneRegex } from '../../constants';
import './utility-lead-form.scss';

const defaultFormValues = {
  company: '',
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  utility_type: '',
  description: '',
};

const UtilityLeadForm = (props) => {
  const [errorMessage, setErrorMessage] = useState('');
  const [submitState, setSubmitState] = useState('');
  const [isDirty, setIsDirty] = useState(false);

  const onSubmit = async (values) => {
    setSubmitState('');

    try {
      await leads.submitPartner({
        ...values,
        referred_by: '',
        type: '',
      });

      setSubmitState('success');
      metrics.trackPartnerFormSubmit(
        window.location.origin + window.location.pathname,
        'UtilityLeadForm'
      );
    } catch (e) {
      Sentry.captureException(e);
      setErrorMessage('We had a problem processing your request. Please try again.');
      setSubmitState('failed');
    }
  };

  const containerClasses = ['utility-f'];
  containerClasses.push('theme-' + props.blok.theme);

  const submitButtonText = props.blok.submit_button_text || 'Submit';
  const submittingButtonText = props.blok.submitting_button_text || 'Submitting...';
  const buttonStyle = props.blok.button_style || 'blue';
  const successMessage = props.blok.success_message || 'Thank you, we will be in touch soon.';

  return (
    <SbEditable content={props.blok}>
      <div className={containerClasses.join(' ')}>
        {submitState !== 'success' && (
          <Formik
            initialValues={defaultFormValues}
            onSubmit={onSubmit}
            validationSchema={Yup.object().shape({
              company: Yup.string().required('Required'),
              firstName: Yup.string().required('Required'),
              lastName: Yup.string().required('Required'),
              phone: Yup.string()
                .required('Required')
                .matches(PhoneRegex, 'Enter your phone number'),
              email: Yup.string().required('Required').email('Enter a valid e-mail address'),
              utility_type: Yup.string().required('Required'),
            })}
          >
            {(formik) => {
              const buttonText = formik.isSubmitting ? submittingButtonText : submitButtonText;

              return (
                <Form className="input-form">
                  <TrackFormDirty
                    formComponent="UtilityLeadForm"
                    isDirty={isDirty}
                    setIsDirty={setIsDirty}
                  />
                  <div className="row align-items-start">
                    <div className="col-12 col-xl-6">
                      <div className="field">
                        <Field name="firstName" placeholder="First Name" />
                        <ErrorMessage className="error" component="div" name="firstName" />
                      </div>
                      <div className="field">
                        <Field name="lastName" placeholder="Last Name" />
                        <ErrorMessage className="error" component="div" name="lastName" />
                      </div>
                      <div className="field">
                        <Field name="phone" placeholder="Phone">
                          {({ field }) => (
                            <InputMask
                              {...field}
                              mask="(999) 999-9999"
                              placeholder="Phone"
                              type="text"
                              alwaysShowMask={false}
                            />
                          )}
                        </Field>
                        <ErrorMessage className="error" component="div" name="phone" />
                      </div>
                      <div className="field">
                        <Field name="email" placeholder="Email" />
                        <ErrorMessage className="error" component="div" type="email" name="email" />
                      </div>
                    </div>
                    <div className="col-12 col-xl-6">
                      <div className="field">
                        <Field name="company" placeholder="Company Name" />
                        <ErrorMessage className="error" component="div" name="company" />
                      </div>
                      <div className="field">
                        <Field as="select" name="utility_type" placeholder="Type">
                          <option value="">Utility type</option>
                          <option value="IOU">IOU</option>
                          <option value="Municipal">Municipal</option>
                          <option value="CoOp">CoOp</option>
                          <option value="CCA">CCA</option>
                          <option value="Other">Other</option>
                        </Field>
                        <ErrorMessage className="error" component="div" name="utility_type" />
                      </div>
                      <div className="field">
                        <Field name="description" as="textarea" rows="4" placeholder="Comments" />
                      </div>
                    </div>
                  </div>
                  {submitState === 'failed' && <div className="error">{errorMessage}</div>}
                  <button
                    className={`btn-${buttonStyle}`}
                    disabled={formik.isSubmitting}
                    type="submit"
                  >
                    {buttonText}
                  </button>
                </Form>
              );
            }}
          </Formik>
        )}
        {submitState === 'success' && <div className="success">{successMessage}</div>}
      </div>
    </SbEditable>
  );
};

export default UtilityLeadForm;
